import React from "react";
import classNames from "classnames";

/**
 * Accessible icon-only button.
 *
 * Replaces the `<div onClick>` pattern across the shell: renders a real
 * <button>, so it is keyboard-operable (Enter/Space), focusable, and announced
 * to screen readers. An accessible name is REQUIRED via `label` — an icon-only
 * control with no text label is invisible to assistive tech.
 *
 * Guarantees the design-system baseline: a 44x44px minimum hit target
 * (WCAG 2.5.5) and a visible focus-visible ring in the brand lime.
 *
 * @param {string}   label      Accessible name (aria-label). Required.
 * @param {"light"|"dark"} tone Surface the button sits on; sets hover + focus
 *                              ring offset so the ring stays visible. Default "light".
 */
export default function IconButton({
    label,
    tone = "light",
    className = "",
    type = "button",
    children,
    ...rest
}) {
    if (process.env.NODE_ENV !== "production" && !label) {
        // eslint-disable-next-line no-console
        console.warn("IconButton: `label` is required for accessibility (aria-label).");
    }

    const Classes = classNames(
        "inline-flex items-center justify-center shrink-0 min-w-11 min-h-11 rounded-full cursor-pointer transition-colors duration-200",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-offset-2",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        {
            "text-black hover:bg-black/5 focus-visible:ring-offset-white": tone === "light",
            "text-white hover:bg-white/10 focus-visible:ring-offset-black": tone === "dark",
        },
        className
    );

    return (
        <button type={type} aria-label={label} className={Classes} {...rest}>
            {children}
        </button>
    );
}
